import { useStore } from '@nanostores/react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { description, db, projectId, projectNameDirty } from './useChatHistory';
import { setProjectDescription } from './db';
import { isMobile } from '@/utils/mobile';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { PencilLine, Check, X } from 'lucide-react';

export function ChatDescription() {
  const { t } = useTranslation();
  const storeDescription = useStore(description);
  const currentProjectId = useStore(projectId);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const trimLen = isMobile() ? 15 : 25;
  const trimmed =
    storeDescription && storeDescription.length > trimLen
      ? storeDescription.slice(0, trimLen) + '...'
      : storeDescription;
  const hasProjectName = Boolean(storeDescription?.trim());

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const commitRename = useCallback(
    async (newName: string) => {
      const trimmedName = newName.trim();

      if (!trimmedName || trimmedName === storeDescription) {
        setIsEditing(false);
        return;
      }

      description.set(trimmedName);

      if (db && currentProjectId) {
        try {
          await setProjectDescription(db, currentProjectId, trimmedName);
        } catch (err) {
          console.error('[ChatDescription] Failed to persist description to IDB:', err);
        }
      }

      projectNameDirty.set(true);
      setIsEditing(false);
    },
    [storeDescription, currentProjectId],
  );

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename(editValue);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleEditClick = () => {
    setEditValue(storeDescription || '');
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1 w-full">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="bg-dualite-background-secondary text-center text-dualite-text-primary text-sm md:text-base border border-dualite-border-primary rounded-sm min-w-[100px] flex-1 py-1"
          maxLength={100}
          aria-label={t('chat.editProjectName')}
        />
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            commitRename(editValue);
          }}
          className="p-0.5 rounded hover:bg-dualite-background-button text-green-500 transition-colors shrink-0"
          aria-label={t('chat.saveProjectName')}
        >
          <Check size={16} strokeWidth={2.5} />
        </button>
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            cancelEditing();
          }}
          className="p-0.5 rounded hover:bg-dualite-background-button text-dualite-text-secondary transition-colors shrink-0"
          aria-label={t('chat.cancelEditProjectName')}
        >
          <X size={16} strokeWidth={2.5} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span className="truncate" title={storeDescription}>
        {trimmed}
      </span>
      {hasProjectName && (
        <button
          onClick={handleEditClick}
          className="p-0.5 rounded hover:bg-dualite-background-button text-dualite-text-secondary transition-colors shrink-0"
          aria-label={t('chat.clickToEditProjectName')}
        >
          <PencilLine size={14} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}
